import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { brand } from '../theme';

// Layout from ARM's sign-in screen: green brand panel + centred white card.
// On phones the panel collapses to a header bar.
const useStyles = makeStyles({
  page: {
    minHeight: '100dvh',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto 1fr',
    backgroundColor: tokens.colorNeutralBackground2,
    '@media (min-width: 861px)': {
      gridTemplateColumns: 'minmax(320px, 5fr) 7fr',
      gridTemplateRows: '1fr',
    },
  },
  // The green fills the whole hero; only its contents stick while the card scrolls.
  brand: { backgroundColor: brand.chrome, color: brand.chromeText },
  brandInner: {
    padding: '6px 16px',
    display: 'flex',
    flexDirection: 'column',
    '@media (min-width: 861px)': {
      justifyContent: 'space-between',
      gap: '40px',
      padding: '48px 56px',
      position: 'sticky',
      top: 0,
      minHeight: '100dvh',
    },
  },
  brandHead: { display: 'flex', alignItems: 'baseline', gap: '10px' },
  wordmark: {
    color: brand.chromeText,
    display: 'inline-block',
    padding: '8px 0',
    fontSize: '1.35rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textDecorationLine: 'none',
    ':hover': { textDecorationLine: 'none' },
    ':focus-visible': { outlineColor: brand.chromeText },
    '@media (min-width: 861px)': { fontSize: '2rem' },
  },
  product: { fontSize: '1rem', fontWeight: 500, '@media (min-width: 861px)': { fontSize: '1.25rem' } },
  brandBody: {
    display: 'none',
    '@media (min-width: 861px)': { display: 'grid', gap: '40px', alignContent: 'center', flexGrow: 1 },
  },
  brandTitle: { margin: 0, fontSize: '2.25rem', fontWeight: 600, lineHeight: 1.15 },
  brandTagline: { margin: '12px 0 0', fontSize: '1.1rem', opacity: 0.95, maxWidth: '440px' },
  brandFoot: { display: 'none', '@media (min-width: 861px)': { display: 'block', margin: 0, fontSize: '0.8rem', opacity: 0.9 } },
  main: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '16px 16px 32px',
    '@media (min-width: 861px)': { justifyContent: 'center', padding: '48px 24px' },
  },
  card: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: '0 8px 24px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)',
    padding: '22px 20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 861px)': { padding: '40px' },
  },
  footer: {
    width: '100%',
    maxWidth: '480px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: '4px 16px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  footerLink: {
    display: 'inline-block',
    padding: '12px 0',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    textDecorationLine: 'underline',
  },
});

interface ShellProps {
  brandTitle: string;
  brandTagline: string;
  brandExtra?: ReactNode;
  footerLeft?: ReactNode;
  children: ReactNode;
  labelledBy?: string;
  /** For pages that render their own footer further down. */
  hideFooter?: boolean;
}

export function Shell({ brandTitle, brandTagline, brandExtra, footerLeft, children, labelledBy, hideFooter }: ShellProps) {
  const s = useStyles();
  return (
    <div className={s.page}>
      <aside className={s.brand}>
        <div className={s.brandInner}>
          <div className={s.brandHead}>
            <RouterLink to="/" className={s.wordmark} aria-label="TELLEC NFR home">
              TELLEC
            </RouterLink>
            <span className={s.product}>NFR</span>
          </div>
          <div className={s.brandBody}>
            <div>
              <p className={s.brandTitle}>{brandTitle}</p>
              <p className={s.brandTagline}>{brandTagline}</p>
            </div>
            {brandExtra}
          </div>
          <p className={s.brandFoot}>TELLEC · Microsoft 365 governance</p>
        </div>
      </aside>
      <main className={s.main}>
        <section className={s.card} aria-labelledby={labelledBy}>
          {children}
        </section>
        {!hideFooter && (
          <footer className={s.footer}>
            {footerLeft ?? <span>© TELLEC</span>}
            <Link className={s.footerLink} href="https://tellec.com/privacy" rel="noopener">
              Privacy
            </Link>
          </footer>
        )}
      </main>
    </div>
  );
}

export function ContactFooter() {
  const s = useStyles();
  return (
    <span>
      Questions?{' '}
      <Link className={s.footerLink} href="mailto:connect@tellec.com">
        connect@tellec.com
      </Link>
    </span>
  );
}

export function BackFooter({ to }: { to: string }) {
  const s = useStyles();
  return (
    <RouterLink to={to} className={s.footerLink} style={{ color: 'inherit' }}>
      Back
    </RouterLink>
  );
}
